import React, { useState } from 'react'
import { Box, Input, InputGroup, InputLeftElement, InputRightElement, InputProps, Text } from '@chakra-ui/core'
import { AlertCircle } from 'lucide-react'

type InputVariant = 'outline' | 'filled' | 'ghost'

export interface ITextInputProps extends Omit<InputProps, 'variant'> {
  variant?: InputVariant
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
  // Color of the border + icon on focus. Defaults to navy.
  focusColor?: string
  // Error state: red border + optional message below
  isInvalid?: boolean
  errorMessage?: string
  // Sizing for the icon slots (default 36px)
  elementHeight?: string
  // Set true when leftIcon is interactive (e.g. a button) — removes pointerEvents="none"
  leftIconClickable?: boolean
  // Set true when rightIcon is interactive (e.g. a clear button)
  rightIconClickable?: boolean
}

// Per-variant base styles applied to the Input
const VARIANT_BASE: Record<InputVariant, Partial<InputProps>> = {
  outline: {
    bg: 'white',
    borderWidth: '1.5px',
    borderStyle: 'solid',
    borderColor: '#E2E8F0',
    borderRadius: '8px',
    _placeholder: { color: '#94A3B8' },
  },
  filled: {
    bg: '#F1F5F9',
    borderWidth: '1.5px',
    borderStyle: 'solid',
    borderColor: 'transparent',
    borderRadius: '8px',
    _placeholder: { color: '#94A3B8' },
    _hover: { bg: '#E8EEF5' },
  },
  // Ghost = just a bottom border, good for inline editing
  ghost: {
    bg: 'transparent',
    border: 'none',
    borderBottom: '1.5px solid',
    borderColor: '#E2E8F0',
    borderRadius: '0',
    px: '0',
    _placeholder: { color: '#94A3B8' },
  },
}

export const TextInput = React.forwardRef<HTMLInputElement, ITextInputProps>(
  (
    {
      variant = 'outline',
      leftIcon,
      rightIcon,
      focusColor = '#0F172A',
      isInvalid,
      errorMessage,
      elementHeight = '36px',
      leftIconClickable = false,
      rightIconClickable = false,
      onFocus,
      onBlur,
      ...rest
    },
    ref
  ) => {
    const [isFocused, setIsFocused] = useState(false)

    // Active color: red when invalid, focusColor otherwise
    const activeColor = isInvalid ? '#DC2626' : focusColor
    const activeShadow = isInvalid ? '0 0 0 3px rgba(220,38,38,0.14)' : `0 0 0 3px ${hexToRgba(focusColor, 0.12)}`

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true)
      onFocus?.(e)
    }

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false)
      onBlur?.(e)
    }

    const iconColor = isInvalid ? '#DC2626' : isFocused ? activeColor : '#94A3B8'

    const hasLeft = Boolean(leftIcon)
    const hasRight = Boolean(rightIcon)

    return (
      <Box w="100%">
        <InputGroup>
          {hasLeft && (
            <InputLeftElement h={elementHeight} w="36px" pointerEvents={leftIconClickable ? 'auto' : 'none'}>
              <Box display="flex" alignItems="center" color={iconColor} style={{ transition: 'color 0.15s ease-out' }}>
                {leftIcon}
              </Box>
            </InputLeftElement>
          )}

          <Input
            ref={ref}
            h={elementHeight}
            pl={hasLeft ? '34px' : undefined}
            pr={hasRight ? '34px' : undefined}
            fontSize="14px"
            color="#0F172A"
            transition="border-color 0.15s ease-out, box-shadow 0.15s ease-out"
            onFocus={handleFocus}
            onBlur={handleBlur}
            isInvalid={isInvalid}
            {...(VARIANT_BASE[variant] as any)}
            // On focus: override border + add glow
            _focus={{
              borderColor: activeColor,
              boxShadow: activeShadow,
              outline: 'none',
              bg: variant === 'filled' ? 'white' : undefined,
            }}
            _invalid={{
              borderColor: '#DC2626',
              boxShadow: 'none',
            }}
            {...rest}
          />

          {hasRight && (
            <InputRightElement h={elementHeight} w="36px" pointerEvents={rightIconClickable ? 'auto' : 'none'}>
              <Box display="flex" alignItems="center" color={iconColor} style={{ transition: 'color 0.15s ease-out' }}>
                {rightIcon}
              </Box>
            </InputRightElement>
          )}
        </InputGroup>

        {isInvalid && errorMessage && (
          <Box display="flex" alignItems="center" mt="4px" style={{ gap: '4px' }}>
            <AlertCircle size={12} color="#DC2626" style={{ flexShrink: 0 }} />
            <Text fontSize="12px" color="#DC2626" lineHeight="1.4">
              {errorMessage}
            </Text>
          </Box>
        )}
      </Box>
    )
  }
)

TextInput.displayName = 'TextInput'

// Converts a hex color like "#0F172A" to rgba(r,g,b,alpha)
function hexToRgba(hex: string, alpha: number): string {
  const clean = hex.replace('#', '')
  const r = parseInt(clean.slice(0, 2), 16)
  const g = parseInt(clean.slice(2, 4), 16)
  const b = parseInt(clean.slice(4, 6), 16)
  return `rgba(${r},${g},${b},${alpha})`
}
